import { RouterStore } from 'mobx-react-router';
import { computed } from 'mobx';
import { ExperimentsStore } from '../../stores/ExperimentsStore';
import { RoutingStore } from './RoutingStore';
import { stripUnknownQueryParams } from './routes/queryParams';
import { CategoriesStore } from './CategoriesStore';
import { RouteLocation } from './routes/createRoute';
import { ConfigStore } from './ConfigStore';

interface RedirectStoreParams {
  routingStore: RoutingStore;
  categoriesStore: CategoriesStore;
  configStore: ConfigStore;
  experimentsStore: ExperimentsStore;
}

export class RedirectStore extends RouterStore {
  private readonly categoriesStore: CategoriesStore;
  private readonly routingStore: RoutingStore;
  private readonly configStore: ConfigStore;
  private readonly experimentsStore: ExperimentsStore;

  constructor({ categoriesStore, routingStore, configStore, experimentsStore }: RedirectStoreParams) {
    super();
    this.categoriesStore = categoriesStore;
    this.routingStore = routingStore;
    this.configStore = configStore;
    this.experimentsStore = experimentsStore;
  }

  @computed
  public get redirect(): {
    location: RouteLocation;
    reason: 'ProperLocation' | 'RenamedCategory' | 'CategoryNotFound' | 'HasParentCategory';
  } | null {
    if (!this.routingStore.location) {
      return null;
    }

    const properLocation = this.redirectProperLocation();
    if (
      properLocation.pathname !== this.routingStore.location.pathname ||
      stripUnknownQueryParams(properLocation.search) !== stripUnknownQueryParams(this.routingStore.location.search)
    ) {
      return {
        location: properLocation,
        reason: 'ProperLocation',
      };
    }

    const parentCategory = this.redirectToParentCategory();
    if (parentCategory) {
      return {
        location: parentCategory,
        reason: 'HasParentCategory',
      };
    }

    const renamedCategory = this.redirectRenamedCategory();
    if (renamedCategory) {
      return {
        location: renamedCategory,
        reason: 'RenamedCategory',
      };
    }

    const categoryNotFound = this.redirectCategoryNotFound();
    if (categoryNotFound) {
      return {
        location: categoryNotFound,
        reason: 'CategoryNotFound',
      };
    }

    return null;
  }

  private redirectCategoryNotFound(): RouteLocation | null {
    if (
      this.routingStore.matchedRoute.routeName === 'category' ||
      this.routingStore.matchedRoute.routeName === 'subCategory'
    ) {
      if (!this.categoriesStore.getCategoryBySlug(this.routingStore.matchedRoute.params.categorySlug)) {
        return this.routingStore.locationBuilders.home({});
      }
    }
    if (this.routingStore.matchedRoute.routeName === 'subCategory') {
      if (!this.categoriesStore.getCategoryBySlug(this.routingStore.matchedRoute.params.subCategorySlug)) {
        return this.routingStore.locationBuilders.category(this.routingStore.matchedRoute.params);
      }
    }
    return null;
  }

  private redirectToParentCategory(): RouteLocation {
    if (this.routingStore.matchedRoute.routeName === 'category') {
      const { categorySlug } = this.routingStore.matchedRoute.params;
      const parentCategory = this.categoriesStore.getParentCategoryBySlug(categorySlug);

      if (parentCategory) {
        return this.routingStore.locationBuilders.subCategory({
          ...this.routingStore.matchedRoute.params,
          categorySlug: parentCategory.name,
          subCategorySlug: categorySlug,
        });
      }
    }
  }

  private redirectProperLocation(): RouteLocation {
    if (this.routingStore.matchedRoute.routeName === 'category') {
      return this.routingStore.locationBuilders.category(this.routingStore.matchedRoute.params);
    }

    if (this.routingStore.matchedRoute.routeName === 'subCategory') {
      return this.routingStore.locationBuilders.subCategory(this.routingStore.matchedRoute.params);
    }

    if (this.routingStore.matchedRoute.routeName === 'search') {
      return this.routingStore.locationBuilders.search(this.routingStore.matchedRoute.params);
    }

    if (
      this.experimentsStore.is('specs.funnel.TemplatesGalleryForYouPage', 'true') &&
      this.routingStore.matchedRoute.routeName === 'forYou'
    ) {
      return this.routingStore.locationBuilders.forYou({});
    }

    return this.routingStore.locationBuilders.home({});
  }

  private redirectRenamedCategory(): RouteLocation | null {
    const { renamedCategories } = this.configStore.config;

    if (this.routingStore.matchedRoute.routeName === 'category') {
      const renameCategory: string | undefined = renamedCategories[this.routingStore.matchedRoute.params.categorySlug];

      if (renameCategory) {
        return this.routingStore.rebuildRoute({ categorySlug: renameCategory });
      }
    }

    if (this.routingStore.matchedRoute.routeName === 'subCategory') {
      const { categorySlug, subCategorySlug } = this.routingStore.matchedRoute.params;
      const renameCategory: string | undefined = renamedCategories[`${categorySlug}/${subCategorySlug}`];

      if (renameCategory) {
        const [renamedCategorySlug, renamedSubCategorySlug] = renameCategory.split('/');
        return this.routingStore.rebuildRoute({
          categorySlug: renamedCategorySlug,
          subCategorySlug: renamedSubCategorySlug,
        });
      }
    }

    return null;
  }
}
